import type * as Api from '@ViewModels';
import { getDisplayName } from '../../../models/NameUtils';
import type { ISupportTicket } from '../../../models/Support/ISupportTicket';
import { TicketPriority, TicketStatus } from '../../../models/Support/SupportEnums';
import { getUserReferenceName } from '../../../utils/Support/supportHelpers';
import { formatSupportTime } from '../../components/supportShared/SupportTimePicker/supportTimePresets';
import { getWebsiteProviderDisplay, isLevitateBuiltWebsite } from '../../../utils/websiteProvider';
import type { MultiColorPillProps } from '../../../web/components-v2/Pill';
import { styles } from './styles';

/**
 * Page-local presentation helpers shared by the inbox panes: score labels for the AI signals,
 * the status → pill mapping, and the timestamp formats used across the list, timeline, and
 * details surfaces.
 */

export type TicketPillColor = MultiColorPillProps['color'];

export const getComplexityInfo = (value: number) => {
	if (value <= 33) return { label: 'Simple', style: styles.aiScoreGreen };
	if (value <= 66) return { label: 'Moderate', style: styles.aiScoreYellow };
	return { label: 'Complex', style: styles.aiScoreRed };
};

export const getConfidenceInfo = (value: number) => {
	if (value <= 33) return { label: 'Low', style: styles.aiScoreRed };
	if (value <= 66) return { label: 'Medium', style: styles.aiScoreYellow };
	return { label: 'High', style: styles.aiScoreGreen };
};

export const getStatusInfo = (status: TicketStatus): { label: string; color: TicketPillColor } => {
	if (status === TicketStatus.Active) return { label: 'Active', color: 'green' };
	if (status === TicketStatus.Pending) return { label: 'Pending', color: 'gray' };
	if (status === TicketStatus.Spam) return { label: 'Spam', color: 'gray' };
	if (status === TicketStatus.Archive) return { label: 'Archive', color: 'gray' };
	if (status === TicketStatus.Snoozed) return { label: 'Snoozed', color: 'blue' };
	return { label: 'Closed', color: 'gray' };
};

/** Pending waits on the customer and Snoozed on a clock; neither needs the agent now, so their rows are dimmed. */
export const isDimmedTicketStatus = (status: TicketStatus): boolean =>
	status === TicketStatus.Pending || status === TicketStatus.Snoozed;

/** The wake time of a Snoozed ticket; undefined for any other status. */
export const getSnoozedUntil = (ticket: Pick<ISupportTicket, 'status' | 'snoozedUntil'>): Date | undefined =>
	ticket.status === TicketStatus.Snoozed && ticket.snoozedUntil ? new Date(ticket.snoozedUntil) : undefined;

/** The status pill text; a Snoozed ticket also names when it wakes. */
export const getStatusPillLabel = (ticket: Pick<ISupportTicket, 'status' | 'snoozedUntil'>, now: Date): string => {
	const snoozedUntil = getSnoozedUntil(ticket);
	return snoozedUntil ? `Snoozed · ${formatSupportTime(snoozedUntil, now)}` : getStatusInfo(ticket.status).label;
};

export const getTicketContactLabel = (ticket: { contactName?: string; contactEmail?: string }): string =>
	ticket.contactName || ticket.contactEmail || '—';

type AccountDetailsInfo = {
	activationStatus?: string;
	additionalInfo?: { shareFileFolderLink?: string; websiteUrl?: string };
	cancellationDate?: Date | string;
	features?: { websites?: { dudaSiteId?: string; enabled?: boolean; provider?: string; websiteUrl?: string } };
};

export interface AccountStatusInfo {
	label: string;
	state: 'active' | 'cancelled' | 'neutral' | 'pending';
	cancellationDate?: string;
}

const getSafeHttpUrl = (value: string | undefined): string | undefined => {
	const trimmedValue = value?.trim();
	if (!trimmedValue) return undefined;

	const urlWithProtocol = /^https?:\/\//i.test(trimmedValue) ? trimmedValue : `https://${trimmedValue}`;
	try {
		const parsedUrl = new URL(urlWithProtocol);
		return parsedUrl.protocol === 'http:' || parsedUrl.protocol === 'https:' ? urlWithProtocol : undefined;
	} catch {
		return undefined;
	}
};

export const getAccountWebsiteUrl = (account: AccountDetailsInfo | undefined): string | undefined =>
	[account?.features?.websites?.websiteUrl, account?.additionalInfo?.websiteUrl]
		.map(getSafeHttpUrl)
		.find((url): url is string => !!url);

/**
 * Withheld for a known Levitate site: those accounts often keep a stale dudaSiteId from before they moved,
 * so the link resolves to an editor holding nothing. An unset provider still gets the link.
 */
export const getDudaEditorUrl = (account: AccountDetailsInfo | undefined): string | undefined => {
	if (isLevitateBuiltWebsite(account?.features?.websites)) {
		return undefined;
	}

	const dudaSiteId = account?.features?.websites?.dudaSiteId?.trim();
	return dudaSiteId ? `https://my.duda.co/home/site/${encodeURIComponent(dudaSiteId)}/home` : undefined;
};

export const getWebsiteProviderInfo = (
	account: AccountDetailsInfo | undefined
): { label: string; color: TicketPillColor } | undefined => {
	if (!account?.features?.websites?.enabled) {
		return undefined;
	}

	return getWebsiteProviderDisplay(account.features.websites);
};

export const getShareFileFolderUrl = (account: AccountDetailsInfo | undefined): string | undefined =>
	getSafeHttpUrl(account?.additionalInfo?.shareFileFolderLink);

export const getAccountStatusInfo = (
	account: AccountDetailsInfo | undefined,
	now: Date = new Date()
): AccountStatusInfo => {
	const cancellationDate = account?.cancellationDate ? new Date(account.cancellationDate) : undefined;
	const cancellationDateIsValid = cancellationDate && !Number.isNaN(cancellationDate.getTime());
	const cancellationDateIso = cancellationDateIsValid ? cancellationDate.toISOString() : undefined;
	const activationStatus = account?.activationStatus?.toUpperCase();

	if (activationStatus === 'CANCELED' || (cancellationDateIsValid && cancellationDate.getTime() <= now.getTime())) {
		return { label: 'Cancelled', state: 'cancelled', cancellationDate: cancellationDateIso };
	}
	if (cancellationDateIsValid) {
		return { label: 'Pending cancellation', state: 'pending', cancellationDate: cancellationDateIso };
	}
	if (activationStatus === 'ACTIVE') return { label: 'Active', state: 'active' };
	if (activationStatus === 'DEACTIVATED') return { label: 'Deactivated', state: 'neutral' };
	if (activationStatus === 'INIT') return { label: 'Initializing', state: 'neutral' };
	return { label: 'Unknown', state: 'neutral' };
};

export const getTicketAssigneeName = (
	ticket: Pick<ISupportTicket, 'assignee' | 'assigneeId'>,
	agentNameMap: Record<string, string>
): string | undefined =>
	getUserReferenceName(ticket.assignee, { includeEmail: false }) ||
	(ticket.assigneeId ? agentNameMap[ticket.assigneeId] : undefined) ||
	ticket.assignee?.email;

/**
 * Pill color per ticket priority. High reads red rather than orange: the desks scan this column for
 * what needs attention now, and orange on the pale tag background was not separating from Normal at
 * a glance. Urgent has to out-rank High in turn, which no tint can do beside another tint — it is
 * rendered as a solid instead, by {@link TicketPriorityPill}, and keeps `red` only as its fallback.
 */
export const PRIORITY_COLOR: Record<TicketPriority, TicketPillColor> = {
	[TicketPriority.Low]: 'gray',
	[TicketPriority.Normal]: 'blue',
	[TicketPriority.High]: 'red',
	[TicketPriority.Urgent]: 'red',
};

/** Compact "2m / 3h / 2d / date" timestamp for the tight list column. */
export const formatRelativeTime = (iso: string): string => {
	if (!iso) return '—';
	const then = new Date(iso).getTime();
	if (Number.isNaN(then)) return '—';
	const diffMin = Math.floor(Math.max(0, Date.now() - then) / 60_000);
	if (diffMin < 1) return 'now';
	if (diffMin < 60) return `${diffMin}m`;
	const diffHr = Math.floor(diffMin / 60);
	if (diffHr < 24) return `${diffHr}h`;
	const diffDays = Math.floor(diffHr / 24);
	if (diffDays < 7) return `${diffDays}d`;
	return new Date(iso).toLocaleDateString();
};

/**
 * Numeric date plus time — the shortest legible stamp. The spelled-out month variant below renders
 * as "Aug 21 at 3:44 PM", ~35px wider, which is the difference between one header line and two on a
 * phone thread card.
 */
export const formatCompactDateTime = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleString(undefined, { month: 'numeric', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Month/day plus time, no year or seconds, for width-constrained rows (hover preview, phone cards). */
export const formatShortDateTime = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });
};

/** Locale date + time for thread/event timestamps; em dash when the value is missing or unparseable. */
export const formatDateTime = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleString();
};

/**
 * Names the zip a message's attachments download into. The archive is read from the response as a blob,
 * so the anchor triggering the save has to supply the name — the API is a separate origin and exposes
 * only `RequestId`, leaving its own `Content-Disposition` name unreadable here.
 */
export const buildAttachmentArchiveFileName = (iso: string | undefined): string => {
	const date = iso ? new Date(iso) : null;
	if (!date || Number.isNaN(date.getTime())) return 'attachments.zip';
	const month = `${date.getMonth() + 1}`.padStart(2, '0');
	const day = `${date.getDate()}`.padStart(2, '0');
	return `attachments-${date.getFullYear()}-${month}-${day}.zip`;
};

/** Locale date (no time) for compact metadata rows; em dash when the value is missing or unparseable. */
export const formatDate = (iso: string | undefined): string => {
	if (!iso) return '—';
	const date = new Date(iso);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleDateString();
};

/**
 * Locale date for a value the API stores as a UTC calendar day (the ads dates are set from a day
 * picker, so they arrive at UTC midnight). Formatting those in local time shows the previous day
 * for every agent west of UTC.
 */
export const formatUtcDate = (value: Date | string | undefined): string => {
	if (!value) return '—';
	const date = new Date(value);
	if (Number.isNaN(date.getTime())) return '—';
	return date.toLocaleDateString(undefined, { timeZone: 'UTC' });
};

type AdsChannelKey = 'facebook' | 'lsa' | 'ppc';

export type AdsAccountInfo = {
	additionalInfo?: {
		adsStage?: string;
		adsSpecialistName?: string;
		adsKickoffDate?: Date | string;
		adsLiveDate?: Date | string;
		adsNoteId?: string;
		lastAdsContact?: Date | string;
		lastAdsMeeting?: Date | string;
		lastAdsMeetingStatus?: string;
		nextAdsMeeting?: Date | string;
		facebookAdsLiveDate?: Date | string;
		googleLocalServiceAdsLiveDate?: Date | string;
		googlePpcAdsLiveDate?: Date | string;
		lastFacebookAdsMeeting?: Date | string;
		lastFacebookAdsMeetingStatus?: string;
		lastGoogleLocalServiceAdsMeeting?: Date | string;
		lastGoogleLocalServiceAdsMeetingStatus?: string;
		lastGooglePpcAdsMeeting?: Date | string;
		lastGooglePpcAdsMeetingStatus?: string;
		nextFacebookAdsMeeting?: Date | string;
		nextGoogleLocalServiceAdsMeeting?: Date | string;
		nextGooglePpcAdsMeeting?: Date | string;
		facebookAdsNoteId?: string;
		googleLocalServiceAdsNoteId?: string;
		googlePpcAdsNoteId?: string;
	};
	features?: {
		ads?: {
			enabled?: boolean;
			facebookAds?: boolean;
			googleLocalServiceAds?: boolean;
			googlePpc?: boolean;
		};
	};
};

/** Mirrors the channel list of the ads module on the account details page. */
const ADS_CHANNELS: {
	key: AdsChannelKey;
	label: string;
	enabledKey: 'facebookAds' | 'googleLocalServiceAds' | 'googlePpc';
	liveDateKey: 'facebookAdsLiveDate' | 'googleLocalServiceAdsLiveDate' | 'googlePpcAdsLiveDate';
	lastMeetingKey: 'lastFacebookAdsMeeting' | 'lastGoogleLocalServiceAdsMeeting' | 'lastGooglePpcAdsMeeting';
	lastStatusKey:
		| 'lastFacebookAdsMeetingStatus'
		| 'lastGoogleLocalServiceAdsMeetingStatus'
		| 'lastGooglePpcAdsMeetingStatus';
	nextMeetingKey: 'nextFacebookAdsMeeting' | 'nextGoogleLocalServiceAdsMeeting' | 'nextGooglePpcAdsMeeting';
	noteIdKey: 'facebookAdsNoteId' | 'googleLocalServiceAdsNoteId' | 'googlePpcAdsNoteId';
}[] = [
	{
		key: 'facebook',
		label: 'Meta Ads',
		enabledKey: 'facebookAds',
		liveDateKey: 'facebookAdsLiveDate',
		lastMeetingKey: 'lastFacebookAdsMeeting',
		lastStatusKey: 'lastFacebookAdsMeetingStatus',
		nextMeetingKey: 'nextFacebookAdsMeeting',
		noteIdKey: 'facebookAdsNoteId',
	},
	{
		key: 'lsa',
		label: 'Google LSA',
		enabledKey: 'googleLocalServiceAds',
		liveDateKey: 'googleLocalServiceAdsLiveDate',
		lastMeetingKey: 'lastGoogleLocalServiceAdsMeeting',
		lastStatusKey: 'lastGoogleLocalServiceAdsMeetingStatus',
		nextMeetingKey: 'nextGoogleLocalServiceAdsMeeting',
		noteIdKey: 'googleLocalServiceAdsNoteId',
	},
	{
		key: 'ppc',
		label: 'Google PPC',
		enabledKey: 'googlePpc',
		liveDateKey: 'googlePpcAdsLiveDate',
		lastMeetingKey: 'lastGooglePpcAdsMeeting',
		lastStatusKey: 'lastGooglePpcAdsMeetingStatus',
		nextMeetingKey: 'nextGooglePpcAdsMeeting',
		noteIdKey: 'googlePpcAdsNoteId',
	},
];

export interface AdsChannelInfo {
	key: AdsChannelKey;
	label: string;
	isLive: boolean;
	liveDate: string;
	lastMeeting: string;
	nextMeeting: string;
	/** Note to show under this channel; empty when neither the channel nor the legacy ads note exists. */
	noteId: string;
}

export interface AdsDetailsInfo {
	channels: AdsChannelInfo[];
	/** Pre-split note that no single channel owns; folded into a channel's own note when there is one. */
	legacyNoteId: string;
	/** Account-wide live state, which the ads module only sets once every enabled channel is live. */
	isLive: boolean;
	liveDate: string;
	/** Carries the no-show suffix the ads module appends when the last meeting was missed. */
	stage: string;
	specialist: string;
	kickoffDate: string;
	lastContact: string;
	lastMeeting: string;
	nextMeeting: string;
}

type NoteByline = {
	creator?: Api.IPrincipal;
	lastModifiedDate?: string;
	creationDate?: string;
	context?: { name?: string; meetingStatus?: string };
};

/** "Patrick Walters · Jul 7, 2026 · Check-in (No showed)" — whichever of those three parts exist. */
export const formatNoteByline = (note: NoteByline): string => {
	const name = note.creator ? getDisplayName(note.creator) : undefined;
	const stamp = note.lastModifiedDate || note.creationDate;
	const date = stamp ? formatDate(stamp) : undefined;
	const meetingName = note.context?.name;
	const meetingStatus = note.context?.meetingStatus;
	const meeting = meetingName && meetingStatus ? `${meetingName} (${meetingStatus})` : meetingName;
	return [name, date, meeting].filter(Boolean).join(' · ');
};

const formatMeetingWithStatus = (value: Date | string | undefined, status: string | undefined): string => {
	const date = formatUtcDate(value);
	return value && status ? `${date} (${status})` : date;
};

const formatMeetingDate = (value: Date | string | undefined): string =>
	value ? formatUtcDate(value) : 'Not scheduled';

/**
 * Read-only ads module summary for the ticket drawer, or nothing when the account does not sell ads.
 * Values come back display-ready so the drawer stays presentational.
 */
export const getAdsDetails = (account: AdsAccountInfo | undefined): AdsDetailsInfo | undefined => {
	const ads = account?.features?.ads;
	if (!ads) return undefined;

	const additionalInfo = account?.additionalInfo;
	const enabledChannels = ADS_CHANNELS.filter(channel => !!ads[channel.enabledKey]);
	if (!ads.enabled && enabledChannels.length === 0) return undefined;

	// A single-channel account predating the per-channel split still keeps its note under the legacy
	// `adsNoteId`, and that note is the channel's note. With several channels there is no way to tell
	// which one the legacy note belongs to, so it is left out.
	const isSingleChannel = enabledChannels.length === 1;
	const legacyNoteId = isSingleChannel ? additionalInfo?.adsNoteId : undefined;
	const channels = enabledChannels.map(channel => {
		const liveDate = additionalInfo?.[channel.liveDateKey];
		return {
			key: channel.key,
			label: channel.label,
			isLive: !!liveDate,
			liveDate: formatUtcDate(liveDate),
			lastMeeting: formatMeetingWithStatus(
				additionalInfo?.[channel.lastMeetingKey],
				additionalInfo?.[channel.lastStatusKey]
			),
			nextMeeting: formatMeetingDate(additionalInfo?.[channel.nextMeetingKey]),
			noteId: additionalInfo?.[channel.noteIdKey] || legacyNoteId || '',
		};
	});

	const stage = additionalInfo?.adsStage || 'None';
	return {
		channels,
		legacyNoteId: isSingleChannel ? '' : additionalInfo?.adsNoteId || '',
		isLive: channels.length > 0 && !!additionalInfo?.adsLiveDate,
		liveDate: formatUtcDate(additionalInfo?.adsLiveDate),
		stage: additionalInfo?.lastAdsMeetingStatus === 'No showed' ? `${stage} (no show)` : stage,
		specialist: additionalInfo?.adsSpecialistName || 'Unassigned',
		kickoffDate: formatUtcDate(additionalInfo?.adsKickoffDate),
		lastContact: formatUtcDate(additionalInfo?.lastAdsContact),
		lastMeeting: formatUtcDate(additionalInfo?.lastAdsMeeting),
		nextMeeting: formatMeetingDate(additionalInfo?.nextAdsMeeting),
	};
};
